// =====================================================================
// TARPI — Single Product Page
// =====================================================================

const { useState: pdpState, useEffect: pdpEffect, useSyncExternalStore: pdpSync } = React;

function pdpCart() {
  return pdpSync(
    (cb) => window.TARPI_store.subscribe(cb),
    () => window.TARPI_store.get(),
    () => window.TARPI_store.get()
  );
}
function pdpCompare() {
  return pdpSync(
    (cb) => window.TARPI_compare.subscribe(cb),
    () => window.TARPI_compare.get(),
    () => window.TARPI_compare.get()
  );
}

// ── Convierte URL de YouTube/Vimeo a embed ───────────────────────────────
function toEmbedUrl(url) {
  if (!url) return null;
  const yt1 = url.match(/youtube\.com\/watch\?v=([\w-]+)/);
  const yt2 = url.match(/youtu\.be\/([\w-]+)/);
  const ytId = (yt1 && yt1[1]) || (yt2 && yt2[1]);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?color=4AB965&title=0&byline=0`;
  return null;
}

// ── Escenario de imagen + galería + lightbox ─────────────────────────────
function ProductImageStage({ product, inCompare }) {
  const hasImg   = !!product.image;
  const allImages = [
    ...(hasImg ? [{ url: product.image, thumb: product.image, alt: product.name }] : []),
    ...(product.gallery || []),
  ];
  const hasGallery  = allImages.length > 0;
  const hasMultiple = allImages.length > 1;

  const [activeIdx, setActiveIdx]       = pdpState(0);
  const [lightboxOpen, setLightboxOpen] = pdpState(false);

  pdpEffect(() => {
    if (!lightboxOpen) {
      // Restaurar scroll al cerrar
      const storedY = document.body.dataset.scrollY || '0';
      document.body.style.overflow   = '';
      document.body.style.position   = '';
      document.body.style.top        = '';
      document.body.style.width      = '';
      window.scrollTo(0, parseInt(storedY, 10));
      return;
    }

    // Bloquear scroll al abrir (preservando posición)
    const scrollY = window.scrollY;
    document.body.dataset.scrollY    = scrollY;
    document.body.style.overflow     = 'hidden';
    document.body.style.position     = 'fixed';
    document.body.style.top          = `-${scrollY}px`;
    document.body.style.width        = '100%';

    const onKey = (e) => {
      if (e.key === 'Escape')     setLightboxOpen(false);
      if (e.key === 'ArrowRight') setActiveIdx(i => Math.min(i + 1, allImages.length - 1));
      if (e.key === 'ArrowLeft')  setActiveIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // Limpiar si el componente se desmonta con lightbox abierto
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.width    = '';
    };
  }, [lightboxOpen, allImages.length]);

  return (
    <React.Fragment>
      <div className={`pdp-bag-stage ${hasMultiple ? 'pdp-bag-stage--gallery' : ''}`}>
        <div className="pdp-bag-shine" aria-hidden="true"></div>
        <div className="pdp-bag-shadow"></div>

        <div
          className={`pdp-bag-wrap ${hasImg ? 'has-real-image' : ''}`}
          onClick={hasGallery ? () => setLightboxOpen(true) : undefined}
          style={hasGallery ? { cursor: 'zoom-in' } : {}}
          role={hasGallery ? 'button' : undefined}
          aria-label={hasGallery ? `Ver imagen de ${product.name}` : undefined}
        >
          {hasGallery ? (
            <div className="pdp-real-img-hero">
              <img src={allImages[activeIdx].url} alt={allImages[activeIdx].alt || product.name} loading="eager" key={activeIdx} />
              <div className="pdp-stage-zoom" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="16" height="16">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </div>
            </div>
          ) : (
            <window.FeedBag line={product.line} name={product.name} category={product.categoryLabel} />
          )}
        </div>

        {hasMultiple && (
          <div className="pdp-stage-thumbs" role="tablist" aria-label="Imágenes del producto">
            {allImages.map((img, i) => (
              <button key={i} className={`pdp-stage-thumb ${i === activeIdx ? 'is-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
                role="tab" aria-selected={i === activeIdx} aria-label={`Imagen ${i + 1}`}>
                <img src={img.thumb || img.url} alt={img.alt || ''} loading="lazy" />
              </button>
            ))}
          </div>
        )}
        {inCompare && <div className="pdp-bag-badge">⇄ En comparador</div>}
      </div>

      {lightboxOpen && hasGallery && (
        <div className="pdp-lightbox" role="dialog" aria-modal="true" onClick={() => setLightboxOpen(false)}>
          <button className="pdp-lightbox-close" onClick={() => setLightboxOpen(false)} aria-label="Cerrar">✕</button>
          {activeIdx > 0 && (
            <button className="pdp-lightbox-prev" onClick={e => { e.stopPropagation(); setActiveIdx(i => i - 1); }} aria-label="Anterior">‹</button>
          )}
          <div className="pdp-lightbox-img-wrap" onClick={e => e.stopPropagation()}>
            <img src={allImages[activeIdx].url} alt={allImages[activeIdx].alt || ''} className="pdp-lightbox-img" />
          </div>
          {activeIdx < allImages.length - 1 && (
            <button className="pdp-lightbox-next" onClick={e => { e.stopPropagation(); setActiveIdx(i => i + 1); }} aria-label="Siguiente">›</button>
          )}
          <div className="pdp-lightbox-counter">{activeIdx + 1} / {allImages.length}</div>
          {hasMultiple && (
            <div className="pdp-lightbox-thumbs">
              {allImages.map((img, i) => (
                <button key={i} className={`pdp-lightbox-thumb ${i === activeIdx ? 'is-active' : ''}`}
                  onClick={e => { e.stopPropagation(); setActiveIdx(i); }} aria-label={`Ver imagen ${i + 1}`}>
                  <img src={img.thumb || img.url} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
}

// ── Componente principal ─────────────────────────────────────────────────
function ProductPage() {
  const _pdpHome   = (window.tarpiData && window.tarpiData.homeUrl) || (location.origin + '/');
  const _pdpAssets = ((window.tarpiData && window.tarpiData.themeUrl) || _pdpHome + 'wp-content/themes/tarpi-theme') + '/assets';
  const _pdpMount  = document.getElementById('product-page');
  const id = (_pdpMount && _pdpMount.dataset.id) || new URLSearchParams(location.search).get('id');
  const product = window.TARPI_PRODUCTS.find(p => p.id === id);

  const cart    = pdpCart();
  const compare = pdpCompare();

  pdpEffect(() => {
    if (product) {
      document.title = `${product.name} · ${product.line.toUpperCase()} · TARPI`;
      document.body.classList.add('has-dark-hero');
    } else {
      document.body.classList.remove('has-dark-hero');
    }
  }, [product]);

  if (!product) {
    return (
      <section style={{padding:'160px var(--gutter)',minHeight:'70vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center'}}>
        <span className="eyebrow">404 · Producto no encontrado</span>
        <h2 className="display display-md" style={{margin:'16px 0 24px',maxWidth:'20ch'}}>No encontramos ese producto en el catálogo TARPI.</h2>
        <a href={`${_pdpHome}#catalogo`} className="btn btn-primary">Volver al catálogo
          <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </a>
      </section>
    );
  }

  const inCart    = !!cart.find(c => c.id === product.id);
  const inCompare = compare.includes(product.id);
  const related   = window.TARPI_PRODUCTS
    .filter(p => p.line === product.line && p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  const embedUrl = toEmbedUrl(product.video);

  // Secciones dinámicas de navegación
  const navSections = [
    ...(product.specs && product.specs.length > 0   ? [{ id:'#pdp-analisis', label:'Análisis' }] : []),
    ...(product.use                                  ? [{ id:'#pdp-uso',      label:'Uso' }] : []),
    ...(product.applicationGuide                     ? [{ id:'#pdp-aplicacion',label:'Aplicación' }] : []),
                                                       { id:'#pdp-info',     label:'Información' },
    ...(product.formats && product.formats.length>0  ? [{ id:'#pdp-formatos',  label:'Formatos' }] : []),
    ...(product.sheetUrl                             ? [{ id:'#pdp-ficha',     label:'Ficha técnica' }] : []),
    ...(embedUrl                                     ? [{ id:'#pdp-video',     label:'Video' }] : []),
  ];

  const stockConf = {
    disponible: { label:'Disponible',  dot:'#4AB965', bg:'rgba(74,185,101,.1)',  color:'#2f8e47' },
    consultar:  { label:'Consultar',   dot:'#D9A441', bg:'rgba(217,164,65,.1)',  color:'#8a5a00' },
    agotado:    { label:'Agotado',     dot:'#E05252', bg:'rgba(224,82,82,.1)',   color:'#8b2020' },
  };

  return (
    <React.Fragment>

      {/* ══════════════ HERO ══════════════ */}
      <section className={`pdp-hero ${product.line}`}>
        <div className="pdp-hero-bg">
          {product.line === 'rumax' && <img src={`${_pdpAssets}/photos/cattle-feeding.jpg`} alt="" />}
          {product.line === 'ovomax' && <img src={`${_pdpAssets}/photos/hens-pen.jpg`} alt="" />}
          {product.line === 'suimax' && <img src={`${_pdpAssets}/photos/pigs-pasture.jpg`} alt="" />}
        </div>
        <div className="pdp-hero-inner">
          <div className="pdp-breadcrumbs">
            <a href={_pdpHome}>Inicio</a><span>/</span>
            <a href={`${_pdpHome}lineas/${product.line}/`}>{product.line.toUpperCase()}</a><span>/</span>
            <span>{product.categoryLabel}</span><span>/</span>
            <span style={{color:'white'}}>{product.name}</span>
          </div>
          <div className="pdp-hero-grid">
            <ProductImageStage product={product} inCompare={inCompare} />
            <div className="pdp-hero-info">
              <span className="lp-eyebrow">{product.line.toUpperCase()} · {product.categoryLabel}</span>
              <h1>{product.name}</h1>
              <p className="pdp-tagline">{product.description}</p>
              <div className="pdp-stage-row">
                {product.species && <div><span className="pdp-meta-label">Especie</span><span className="pdp-meta-val">{product.species}</span></div>}
                {product.stage && <div><span className="pdp-meta-label">Etapa</span><span className="pdp-meta-val">{product.stage}</span></div>}
                {product.validity && <div><span className="pdp-meta-label">Validez</span><span className="pdp-meta-val">{product.validity.replace(/ desde su elaboraci[oó]n\.?/,'').replace(/ desde la fecha de fabricaci[oó]n\.?/,'').replace(/,/,'').trim()}</span></div>}
              </div>
              {product.highlights && product.highlights.length > 0 && (
                <div className="pdp-highlights">
                  {product.highlights.map((h, i) => <span key={i} className="pdp-pill">{h}</span>)}
                </div>
              )}
              {product.certifications && (
                <div className="pdp-cert-strip">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="14" height="14"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
                  {product.certifications}
                </div>
              )}
              {product.formats && product.formats.length > 0 && (
                <div className="pdp-format-pills">
                  {product.formats.map((f, i) => <span key={i} className="pdp-format-pill">{f.name}</span>)}
                </div>
              )}
              <div className="pdp-hero-actions">
                <button className={`btn btn-primary ${inCart ? 'disabled' : ''}`} onClick={() => window.TARPI_store.add(product)}>
                  {inCart ? '✓ En cotización' : 'Agregar a cotización'}
                  <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                </button>
                <button className="btn btn-ghost light" onClick={() => window.TARPI_compare.toggle(product.id)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="7" height="16" rx="1"/><rect x="14" y="4" width="7" height="16" rx="1"/></svg>
                  {inCompare ? 'En comparador' : 'Comparar'}
                </button>
                {product.sheetUrl && (
                  <a href={product.sheetUrl} target="_blank" rel="noopener noreferrer" download className="btn btn-ghost light pdp-sheet-hero-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="15" height="15"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Ficha técnica
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ DESCRIPCIÓN EXTENDIDA ══════════════ */}
      {(product.marketingText || (product.benefits && product.benefits.length > 0)) && (
        <section className="pdp-overview">
          <div className="pdp-overview__inner">
            {product.marketingText && (
              <div className="pdp-marketing-text" dangerouslySetInnerHTML={{ __html: product.marketingText }} />
            )}
            {product.benefits && product.benefits.length > 0 && (
              <div className="pdp-benefits">
                {product.benefits.map((b, i) => (
                  <div className="pdp-benefit" key={i}>
                    <div className="pdp-benefit__icon">{b.icon || '✓'}</div>
                    <div className="pdp-benefit__content">
                      <h3 className="pdp-benefit__title">{b.title}</h3>
                      {b.description && <p className="pdp-benefit__desc">{b.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══════════════ BARRA DE NAVEGACIÓN ══════════════ */}
      {navSections.length > 0 && (
        <nav className="pdp-snav" aria-label="Secciones del producto">
          <div className="pdp-snav__inner">
            {navSections.map(s => (
              <a key={s.id} href={s.id} className="pdp-snav__pill">{s.label}</a>
            ))}
          </div>
        </nav>
      )}

      <div className="pdp-details-wrap">

        {/* ── 01 · ANÁLISIS NUTRICIONAL ── */}
        {product.specs && product.specs.length > 0 && (() => {
          const numeric = product.specs.filter(s => !isNaN(parseFloat(s.value)));
          const maxVal  = numeric.length > 0 ? Math.max(...numeric.map(s => parseFloat(s.value))) : 0;
          return (
            <section id="pdp-analisis" className="pdp-det-block pdp-det-block--white reveal">
              <div className="pdp-det-inner">
                <div className="pdp-det-header">
                  <div className="pdp-det-num-wrap"><span className="pdp-det-num-bg">01</span></div>
                  <div className="pdp-det-heading">
                    <h2 className="pdp-det-title">Análisis nutricional</h2>
                    <p className="pdp-det-sub">Composición declarada por análisis de laboratorio · Valores por kg de producto</p>
                  </div>
                </div>
                <div className="pdp-specs-grid-v3">
                  {product.specs.map((s, i) => {
                    const numVal = parseFloat(s.value);
                    const pct = !isNaN(numVal) && maxVal > 0 ? Math.round(numVal / maxVal * 100) : 0;
                    return (
                      <div className="pdp-spec-v3" key={i}>
                        <div className="pdp-spec-v3__label">{s.label}</div>
                        <div className="pdp-spec-v3__value">
                          {s.value}
                          <span className="pdp-spec-v3__unit">{s.unit && ` ${s.unit}`}</span>
                        </div>
                        {pct > 0 && (
                          <div className="pdp-spec-v3__bar">
                            <div className="pdp-spec-v3__fill" style={{width:`${pct}%`}}></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })()}

        {/* ── 02 · MODO DE USO ── */}
        {product.use && (
          <section id="pdp-uso" className="pdp-det-block pdp-det-block--cream reveal">
            <div className="pdp-det-inner">
              <div className="pdp-det-header">
                <div className="pdp-det-num-wrap"><span className="pdp-det-num-bg">02</span></div>
                <div className="pdp-det-heading">
                  <h2 className="pdp-det-title">Modo de uso</h2>
                  <p className="pdp-det-sub">Instrucciones de administración y manejo.</p>
                </div>
              </div>
              <div className="pdp-use-block">
                <div className="pdp-use-accent"></div>
                <p className="pdp-use-text">{product.use}</p>
              </div>
            </div>
          </section>
        )}

        {/* ── 03 · GUÍA DE APLICACIÓN ── */}
        {product.applicationGuide && (
          <section id="pdp-aplicacion" className="pdp-det-block pdp-det-block--white reveal">
            <div className="pdp-det-inner">
              <div className="pdp-det-header">
                <div className="pdp-det-num-wrap"><span className="pdp-det-num-bg">03</span></div>
                <div className="pdp-det-heading">
                  <h2 className="pdp-det-title">Guía de aplicación</h2>
                  <p className="pdp-det-sub">Dosificación recomendada según categoría animal.</p>
                </div>
              </div>
              <div className="pdp-application-guide" dangerouslySetInnerHTML={{ __html: product.applicationGuide }} />
            </div>
          </section>
        )}

        {/* ── 04 · INFORMACIÓN DEL PRODUCTO ── */}
        <section id="pdp-info" className="pdp-det-block pdp-det-block--cream reveal">
          <div className="pdp-det-inner">
            <div className="pdp-det-header">
              <div className="pdp-det-num-wrap"><span className="pdp-det-num-bg">04</span></div>
              <div className="pdp-det-heading">
                <h2 className="pdp-det-title">Información del producto</h2>
              </div>
            </div>
            <div className="pdp-meta-cards">
              {product.species && (
                <div className="pdp-meta-card">
                  <div className="pdp-meta-card__ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="18" height="18"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
                  <div className="pdp-meta-card__lbl">Especie objetivo</div>
                  <div className="pdp-meta-card__val">{product.species}</div>
                </div>
              )}
              {product.stage && (
                <div className="pdp-meta-card">
                  <div className="pdp-meta-card__ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="18" height="18"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
                  <div className="pdp-meta-card__lbl">Etapa productiva</div>
                  <div className="pdp-meta-card__val">{product.stage}</div>
                </div>
              )}
              {product.validity && (
                <div className="pdp-meta-card">
                  <div className="pdp-meta-card__ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="18" height="18"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
                  <div className="pdp-meta-card__lbl">Validez</div>
                  <div className="pdp-meta-card__val">{product.validity}</div>
                </div>
              )}
              <div className="pdp-meta-card">
                <div className="pdp-meta-card__ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="18" height="18"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></div>
                <div className="pdp-meta-card__lbl">Categoría</div>
                <div className="pdp-meta-card__val">{product.categoryLabel}</div>
              </div>
              <div className="pdp-meta-card">
                <div className="pdp-meta-card__ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="18" height="18"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></div>
                <div className="pdp-meta-card__lbl">Conservación</div>
                <div className="pdp-meta-card__val">Lugar seco y ventilado, sin rayos solares directos</div>
              </div>
              <div className="pdp-meta-card">
                <div className="pdp-meta-card__ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" width="18" height="18"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg></div>
                <div className="pdp-meta-card__lbl">Línea</div>
                <div className="pdp-meta-card__val">{product.line.toUpperCase()} — Nutrición Animal</div>
              </div>
            </div>
            {product.restrictions && (
              <div className="pdp-restrictions-v2">
                <div className="pdp-restrictions-v2__head">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="14" height="14"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  Restricciones y recomendaciones de uso
                </div>
                <p className="pdp-restrictions-v2__text">{product.restrictions}</p>
              </div>
            )}
          </div>
        </section>

        {/* ── 05 · FORMATOS DISPONIBLES — siempre visible ── */}
        <section id="pdp-formatos" className="pdp-det-block pdp-det-block--white reveal">
          <div className="pdp-det-inner">
            <div className="pdp-det-header">
              <div className="pdp-det-num-wrap"><span className="pdp-det-num-bg">05</span></div>
              <div className="pdp-det-heading">
                <h2 className="pdp-det-title">Formatos disponibles</h2>
                <p className="pdp-det-sub">Presentaciones comerciales disponibles. Consultá disponibilidad con tu representante TARPI.</p>
              </div>
            </div>

            {product.formats && product.formats.length > 0 ? (
              <div className="pdp-formats-v2">
                {product.formats.map((f, i) => {
                  const sc = stockConf[f.stock] || stockConf.disponible;
                  return (
                    <div className="pdp-format-v2" key={i}>
                      <div className="pdp-format-v2__top">
                        <div className="pdp-format-v2__icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="20" height="20">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                            <line x1="12" y1="22.08" x2="12" y2="12"/>
                          </svg>
                        </div>
                        <div className="pdp-format-v2__stock" style={{background:sc.bg,color:sc.color}}>
                          <span style={{width:6,height:6,borderRadius:'50%',background:sc.dot,display:'inline-block',marginRight:5}}></span>
                          {sc.label}
                        </div>
                      </div>
                      <div className="pdp-format-v2__name">{f.name}</div>
                      {f.weight && <div className="pdp-format-v2__detail">Contenido: <strong>{f.weight}</strong></div>}
                      {f.sku && <div className="pdp-format-v2__detail pdp-format-v2__sku">SKU: <code>{f.sku}</code></div>}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Estado vacío: guía para cargar datos */
              <div className="pdp-formats-empty">
                <div className="pdp-formats-empty__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" width="32" height="32">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                  </svg>
                </div>
                <div className="pdp-formats-empty__body">
                  <p className="pdp-formats-empty__title">Formatos por definir</p>
                  <p className="pdp-formats-empty__sub">
                    Consultá los formatos disponibles directamente con nuestro equipo comercial.
                  </p>
                  <a
                    href="https://wa.me/595974931681"
                    target="_blank" rel="noopener noreferrer"
                    className="pdp-formats-empty__cta"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
                      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2"/>
                    </svg>
                    Consultar por WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── 06 · FICHA TÉCNICA ── */}
        {product.sheetUrl && (
          <section id="pdp-ficha" className="pdp-sheet-section reveal">
            <div className="pdp-det-inner">
              <div className="pdp-sheet-card">
                <div className="pdp-sheet-card__left">
                  <div className="pdp-sheet-card__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" width="32" height="32"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="11" y2="17"/></svg>
                  </div>
                  <div className="pdp-sheet-card__info">
                    <h3 className="pdp-sheet-card__title">Ficha técnica</h3>
                    <p className="pdp-sheet-card__name">{product.sheetName || `${product.name} — Ficha técnica.pdf`}</p>
                    {product.sheetSize && <p className="pdp-sheet-card__size">PDF · {product.sheetSize}</p>}
                  </div>
                </div>
                <a href={product.sheetUrl} target="_blank" rel="noopener noreferrer" download className="pdp-sheet-card__btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="17" height="17"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  {product.sheetLabel || 'Descargar'}
                </a>
              </div>
            </div>
          </section>
        )}

        {/* ── 07 · VIDEO ── */}
        {embedUrl && (
          <section id="pdp-video" className="pdp-det-block pdp-det-block--cream reveal">
            <div className="pdp-det-inner">
              <div className="pdp-det-header">
                <div className="pdp-det-num-wrap"><span className="pdp-det-num-bg">07</span></div>
                <div className="pdp-det-heading">
                  <h2 className="pdp-det-title">{product.videoTitle || 'Video del producto'}</h2>
                </div>
              </div>
              <div className="pdp-video-frame">
                <iframe src={embedUrl} title={product.videoTitle || 'Video del producto'} frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen loading="lazy" />
              </div>
            </div>
          </section>
        )}

      </div>{/* /pdp-details-wrap */}

      {/* ══════════════ PRODUCTOS RELACIONADOS ══════════════ */}
      {related.length > 0 && (
        <section className="pdp-related">
          <div className="pdp-related-head reveal">
            <span className="eyebrow">Productos relacionados</span>
            <h3>Otros {product.categoryLabel.toLowerCase()} de la línea {product.line.toUpperCase()}</h3>
          </div>
          <div className="pdp-related-grid">
            {related.map(r => (
              <a key={r.id} href={`${_pdpHome}product/${r.id}/`} className={`featured-card ${r.line}`}>
                <div className="featured-visual">
                  {r.image ? (
                    <div className="featured-real-img"><img src={r.image} alt={r.name} loading="lazy" /></div>
                  ) : (
                    <div className="featured-bag"><window.FeedBag line={r.line} name={r.name} category={r.categoryLabel} /></div>
                  )}
                </div>
                <div className="featured-info">
                  <span className="featured-line">{r.line.toUpperCase()} · {r.categoryLabel}</span>
                  <h4 className="featured-name">{r.name}</h4>
                  <div className="featured-meta">
                    <span>{r.species}</span>
                    <span className="featured-arrow">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

    </React.Fragment>
  );
}

const pdpMount = document.getElementById('product-page');
if (pdpMount) ReactDOM.createRoot(pdpMount).render(<ProductPage />);
